import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiSearch, FiClock, FiArrowRight } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

const SearchModal = ({ isOpen, onClose }) => {
  const [searchValue, setSearchValue] = useState('');
  const [recentSearches, setRecentSearches] = useState([]);
  const inputRef = useRef(null);
  const modalRef = useRef(null);
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  // Load recent searches from localStorage
  useEffect(() => {
    const savedSearches = localStorage.getItem('recentSearches');
    if (savedSearches) {
      setRecentSearches(JSON.parse(savedSearches));
    }
  }, []);

  // Save recent searches to localStorage
  useEffect(() => {
    if (recentSearches.length > 0) {
      localStorage.setItem('recentSearches', JSON.stringify(recentSearches));
    }
  }, [recentSearches]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      setTimeout(() => inputRef.current?.focus(), 100);
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [isOpen]);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'Enter' && searchValue.trim()) handleSearch();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose, searchValue]);

  // Close when clicking outside modal
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  const handleClear = () => setSearchValue('');

  const handleSearch = () => {
    if (!searchValue.trim()) return;

    // Add to recent searches (limit to 5 items)
    const updatedSearches = [
      searchValue,
      ...recentSearches.filter(item => item !== searchValue)
    ].slice(0, 5);
    document.body.style.overflow = 'unset';
    setRecentSearches(updatedSearches);
    navigate(`/search?q=${encodeURIComponent(searchValue)}`);
    onClose();
  };



  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop with subtle blur */}
          <motion.div
            className="fixed inset-0 min-h-screen bg-black/70 backdrop-blur-md z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />

          {/* Modal Container */}
          <div className="fixed inset-0 flex items-start justify-center mt-16 px-4 z-50">
            <motion.div
              ref={modalRef}
              className="bg-white dark:bg-gray-900 w-full max-w-2xl rounded-2xl shadow-2xl overflow-hidden border border-gray-200 dark:border-gray-700"
              initial={{ opacity: 0, y: -50, scale: 0.95 }}
              animate={{
                opacity: 1,
                y: 0,
                scale: 1,
                transition: {
                  type: "spring",
                  damping: 25,
                  stiffness: 300,
                  mass: 0.5
                }
              }}
              exit={{
                opacity: 0,
                y: -30,
                transition: { duration: 0.2 }
              }}
            >
              {/* Search Header */}
              <div className="relative bg-gradient-to-r from-teal-50 to-indigo-50 dark:from-gray-800 dark:to-gray-800 px-6 py-5 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center">
                  <div className="relative flex-1 flex items-center bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-300 dark:border-gray-600 focus-within:ring-2 focus-within:ring-teal-500 focus-within:border-transparent overflow-hidden">

                    <input
                      ref={inputRef}
                      type="text"
                      value={searchValue}
                      onChange={(e) => setSearchValue(e.target.value)}
                      placeholder={t('searchPlaceholder') || "Search for anything..."}
                      className="w-full  text-gray-900 dark:text-white dark:bg-gray-800 px-4 focus:outline-none placeholder-gray-500 dark:placeholder-gray-400"
                    />
                    {searchValue && (
                      <motion.button
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        onClick={handleClear}
                        className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 mr-2"
                      >
                        <FiX className="w-4 h-4 text-gray-500" />
                      </motion.button>
                    )}
                    <button
                      onClick={handleSearch}
                      disabled={!searchValue.trim()}
                      className={`px-5 py-3 font-medium rounded-r-lg transition-all ${searchValue.trim() ? 'bg-teal-600 hover:bg-teal-700 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'}`}
                    >
                      {t('search') || "Search"}
                    </button>
                  </div>

                </div>
              </div>



              {/* Footer */}
              <div className="px-6 py-4 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-200 dark:border-gray-700 text-right">
                <button
                  onClick={onClose}
                  className="px-5 py-2.5 rounded-lg font-medium text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 hover:bg-white dark:hover:bg-red-700 transition-colors"
                >
                  {t('close') || "Close"}
                </button>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};

export default SearchModal;